import { useEffect, useState } from "react";
import { MessageCircle, Link2, Copy, Eye, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtPrice } from "@/lib/totals";

/** Iter 152 — Enlace público del presupuesto: WhatsApp, copiar y estado "visto". */
export default function QuoteLinkPanel({ repairId, customer, onUpdated }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { const r = await api.get(`/repairs/${repairId}/quote-link`); setInfo(r.data); } catch { /* no-op */ }
  };
  useEffect(() => { if (repairId) refresh(); /* eslint-disable-next-line */ }, [repairId]);

  const ensureLink = async () => {
    const r = await api.post(`/repairs/${repairId}/quote-link`);
    return r.data.url;
  };
  const copyLink = async () => {
    try {
      const url = await ensureLink();
      await navigator.clipboard?.writeText(url);
      toast.success("Enlace copiado");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const sendWhatsApp = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/repairs/${repairId}/quote-link/whatsapp`);
      window.open(r.data.wa_link, "_blank", "noopener");
      toast.success("WhatsApp abierto con el enlace del presupuesto");
      refresh();
      try { const rr = await api.get(`/repairs/${repairId}`); onUpdated?.(rr.data); } catch { /* no-op */ }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const opens = info?.opens || {};
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
  const stateLabel = {
    pending: ["Pendiente de respuesta", Clock, "text-[#8A6100]"],
    accepted: ["Aceptado por el cliente", CheckCircle2, "text-[#3F5222]"],
    rejected: ["Rechazado por el cliente", XCircle, "text-[#9B2C1F]"],
    expired: ["Enlace caducado", Clock, "text-neutral-500"],
  }[info?.state] || null;
  const StateIcon = stateLabel?.[1];

  return (
    <div className="pt-2 border-t border-[#E5E5E0] space-y-2" data-testid="quote-link-panel">
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] flex items-center gap-1">
        <Link2 size={11} /> Enlace público del presupuesto
      </label>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={sendWhatsApp} disabled={busy || !(customer?.phone || "").trim()}
          data-testid="repair-quote-whatsapp" title={!(customer?.phone || "").trim() ? "El cliente no tiene teléfono" : ""}
          className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50">
          <MessageCircle size={14} /> {busy ? "Preparando…" : "Enviar por WhatsApp"}
        </button>
        <button type="button" onClick={copyLink} data-testid="repair-quote-copy-link"
          className="inline-flex items-center justify-center gap-2 border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-sm font-semibold">
          <Copy size={14} /> Copiar enlace
        </button>
      </div>
      {info?.exists && stateLabel && (
        <div className="text-xs space-y-0.5" data-testid="quote-link-status">
          <div className={`inline-flex items-center gap-1 font-semibold ${stateLabel[2]}`}>
            <StateIcon size={12} /> {stateLabel[0]}
          </div>
          <div className="text-[#525252] inline-flex items-center gap-1 ml-3">
            <Eye size={12} />
            {opens.count > 0 ? `Visto por el cliente ${opens.count} ${opens.count > 1 ? "veces" : "vez"} · última ${fmt(opens.last_opened_at)}` : "Todavía no abierto por el cliente"}
          </div>
          {info.selection?.count > 0 && (
            <div className="text-[#3F5222] ml-3" data-testid="quote-link-addons">
              {info.selection.count} extra(s) seleccionado(s) · {fmtPrice(info.selection.total)} {info.selection.status === "ready_to_process" ? "· pendientes de procesar" : "· provisional"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
